import {
  Box,
  Card as MuiCard,
  Popper,
  Typography,
  useTheme,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { useState } from "react";

import { PMBaseCardProps } from "../pm-base-card/pm-base-card";
import { type ButtonVariant, PMButton } from "../pm-button/pm-button";
import { checkColorContrast, MaskedIcon, resolveIcon } from "../utils";

export interface PMTitleCardProps extends Omit<
  PMBaseCardProps,
  "children" | "showInfoIcon" | "infoIcon" | "showMenuIcon" | "menuIcon"
> {
  /**
   * An Array of buttons to be displayed on the right-hand side of the card.
   * buttonConfigs.label - The label of the button.
   * buttonConfigs.onClick - The click handler of the button.
   */
  buttonConfigs?: {
    label: string;
    onClick: () => void;
    variant?: ButtonVariant;
  }[];

  /**
   * An Array of badges to be displayed on the right-hand side of the card.
   * badgeConfigs.label - The label of the badge
   * badgeConfigs.icon - The icon of the badge - String keys are resolved against
   * `@mui/icons-material` (e.g. `"MoreVert"`).
   * badgeConfigs.iconColor The icon color of the badge.
   * badgeConfigs.backgroundColor The background color of the badge.
   * badgeConfigs.color The text color of the badge.
   */
  badgeConfigs?: BadgeConfig[];
}

export type BadgeConfig = {
  label: string;
  icon?: string;
  muiIcon?: string;
  iconColor?: string;
  backgroundColor: string;
  color: string;
  hoverText?: string;
};

/**
 * A flexible card component that renders:
 *  - An optional left-aligned icon and title
 *  - Optional right-aligned badges/chips and buttons
 *
 * @param props.icon           Icon node or string key to resolve from MUI Icons.
 * @param props.title          Title text or custom node.
 * @param props.fixedWidth     Width of the card; numbers are in px. @default "100%"
 * @param props.fixedHeight    Height of the card; numbers are in px. @default "64px"
 * @param props.buttonConfigs  Config options for the buttons on the right-hand side.
 * @param props.badgeConfigs   Config options for the badges/chips on the right-hand side.
 * @returns A MUI Card component styled with title and slots for a badge and buttons on the right-hand side.
 */
export const PMTitleCard: PimoReactComponent<PMTitleCardProps> = ({
  icon,
  title,
  fixedWidth = "100%",
  fixedHeight = "64px",
  overflow = "hidden",
  rightSlotOverride,
  style,
  buttonConfigs,
  badgeConfigs,
  ...props
}) => {
  const resolvedIcon = resolveIcon(icon);

  const theme = useTheme();

  const leftSlot = (
    <Box
      sx={{ display: "flex", alignItems: "flex-start", flex: 1, minWidth: 0 }}
    >
      {resolvedIcon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: "4px",
            flexShrink: 0,
          }}
        >
          {resolvedIcon}
        </Box>
      )}
      {title &&
        (typeof title === "string" ? (
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.text.primary,
              wordWrap: "break-word",
              overflowWrap: "break-word",
              hyphens: "auto",
              lineHeight: 1.2,
              flex: 1,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
        ) : (
          title
        ))}
    </Box>
  );

  const rightSlot = rightSlotOverride ?? (
    <Box
      sx={{ display: "flex", gap: "12px", alignItems: "center", flexShrink: 0 }}
    >
      {(badgeConfigs ?? []).map(
        (
          {
            color,
            label,
            icon,
            iconColor,
            muiIcon,
            backgroundColor,
            hoverText,
          },
          index
        ) => (
          <Chip
            key={index}
            label={label}
            icon={icon}
            muiIcon={muiIcon}
            iconColor={iconColor}
            backgroundColor={backgroundColor}
            color={color}
            hoverText={hoverText}
          />
        )
      )}
      {(buttonConfigs ?? []).map(
        ({ label, onClick, variant = "contained" }) => (
          <PMButton
            key={label}
            size="medium"
            variant={variant}
            text={label}
            onClick={onClick}
          />
        )
      )}
    </Box>
  );

  const { fireEvent, ...cardProps } = props;
  void fireEvent;

  return (
    <MuiCard
      {...cardProps}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 6,
        boxShadow: "none",
        width: fixedWidth,
        height: fixedHeight,
        overflow,
        ...style,
      }}
    >
      {(resolvedIcon || title) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "100%",
            p: 2,
            gap: 2,
          }}
        >
          {leftSlot}
          {rightSlot}
        </Box>
      )}
    </MuiCard>
  );
};

const Chip = ({
  label,
  icon,
  muiIcon,
  backgroundColor,
  color,
  hoverText,
}: BadgeConfig) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const resolvedMuiIcon = resolveIcon(muiIcon);

  const contrastTextColor = checkColorContrast({
    backgroundColor,
    textColor: color,
  })?.textColor;

  return (
    <Box
      data-testid="ChipComponent"
      onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
      onMouseLeave={() => setAnchorEl(null)}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "left",
          height: "2em",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            py: "auto",
            px: "1em",
            borderRadius: "12px",
            backgroundColor: backgroundColor,
            display: "flex",
            alignItems: "center",
            fontWeight: 500,
            lineHeight: "2em",
            height: "2em",
            overflow: "hidden",
            color: contrastTextColor || color,
            whiteSpace: "nowrap",
          }}
        >
          {label}
          {icon ? (
            <MaskedIcon
              src={icon}
              color={contrastTextColor || color}
              style={{ marginLeft: 4 }}
            />
          ) : (
            resolvedMuiIcon && (
              <Box sx={{ display: "flex", alignItems: "center", pl: 1 }}>
                {resolvedMuiIcon}
              </Box>
            )
          )}
        </Typography>
      </Box>

      {hoverText && (
        <Popper
          id="info-popover"
          open={open}
          anchorEl={anchorEl}
          sx={{ pointerEvents: "auto" }}
        >
          <Box
            sx={{
              p: "12px 16px",
              maxWidth: 300,
              borderRadius: "8px",
              backgroundColor: "#fff",
              border: "1px solid #fff",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "24px",
                textAlign: "center",
                ml: "8px",
                mr: "8px",
              }}
            >
              {hoverText}
            </Typography>
          </Box>
        </Popper>
      )}
    </Box>
  );
};

export default PMTitleCard;
